import * as Core from "@zeller-public/payments-sdk-core";
import { Suspense, useEffect, useState } from "react";

import { loginCallback, logoutCallback } from "./@lib-auth";
import { AuthenticationOperationFailure } from "./app/authentication-operation-failure";
import { Routes } from "./app/routes";
import { logger } from "./shared/logger";
import * as transport from "./transport";

export function App() {
	const [request, setRequest] = useState<Core.Protocol.Request_Query>();

	/**
	 * It is acceptable for this to sit seperate from `request` state as a
	 * non-explicitly-mutually-exclusive state layer as to not interupt the
	 * existing `request` `useEffect`-driven behaviours.
	 */
	const [error, setError] = useState<
		{
			$type: "AuthenticationOperationFailure";
		} & AuthenticationOperationFailure.Props
	>();

	useEffect(() => {
		window._setRequest = setRequest;

		transport.Ok();
		const interval = setInterval(transport.Ok, 1000);

		(async () => {
			// Which path the host mounted on. Path + param presence only,
			// never tokens/PII.
			logger.info("app: mount", {
				"zeller.app.pathname": window.location.pathname,
				"zeller.app.hasCode": new URL(window.location.href).searchParams.has(
					"code",
				),
				"zeller.app.hasIss": new URL(window.location.href).searchParams.has(
					"iss",
				),
			});

			if (window.location.pathname === "/login-callback") {
				return loginCallback();
			}

			if (window.location.pathname === "/logout-callback") {
				return logoutCallback();
			}

			/**
			 * When Third-Party Cookies are disabled in the user agent Auth0 fails
			 * fallback redirects to the Auth0 Application's "Application Login URI".
			 * We can detect this error state because Auth0 adds the `?iss=` query
			 * parameter when redirecting away from the failed login flow.
			 */
			const params = new URL(window.location.href).searchParams;
			if (params.get("iss")) {
				/**
				 * @interactive
				 * It is always safe to show a blocking UI state for this condition
				 * (regardless of if we can or can't restore the query object) because
				 * the login-flow and its redirections are always interactive.
				 */
				return void (() => {
					setError({
						$type: "AuthenticationOperationFailure",
						/**
						 * Cannot currently handle "Try again" because no request handle.
						 * https://npco-dev.atlassian.net/browse/ISDK-1887
						 */
						// onRetry: () => login(request),
						onClose: () => {
							setError(undefined);
							transport.Resolve.Error("Operation Failure");
						},
					});
				})();
			}
		})();

		return () => {
			window._setRequest = undefined;
			clearInterval(interval);
		};
	}, [setRequest]);

	if (error?.$type === "AuthenticationOperationFailure") {
		return <AuthenticationOperationFailure {...error} />;
	}

	if (!request) {
		return null;
	}

	return (
		<Suspense fallback={null}>
			<Routes request={request} />
		</Suspense>
	);
}
